import React, { useEffect, useRef, useState } from "react";
import ScreenHeader from "../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import DescriptionText from "../../../CommonComponents/DescriptionText";
import IconContainer from "../../../CommonComponents/IconContainer";
import { PlusOutlined } from "@ant-design/icons";
import { isMobile } from "../../../utils/helper";
import { DatePicker, Skeleton } from "antd";
import UploadConfirmationModal from "./UploadConfirmationModal";
import {
  deleteBothImageCopies,
  updateUploaderImageCount,
} from "../../../services/firebaseService/endPoints/clubAdmin/gallery";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import useUserStore, { selectUserDetails } from "../../../store/user/user";
import dayjs from "dayjs";
import ImageGallery from "../../../CommonComponents/ImageGallery";
import imageCompression from "browser-image-compression";
import {
  fetchAdminGalleryPagedImages,
  uploadImageSizes,
} from "../../../services/firebaseService/endPoints/admin/gallery";
import { collection, doc, writeBatch } from "firebase/firestore";
import { db } from "../../../services/firebaseService/connection";
import logger from "../../../utils/logger";
import {
  GUIDE_SETUP,
  TOPIC_UPLOAD_PHOTOS,
} from "../../../constants/gettingStarted";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "./Gallery.css";
import heic2any from "heic2any";
import NoImages from "./NoImages";
import useMembersData from "../members/Members/useMembersData";
import { toast } from "react-toastify";
import { useInfiniteQuery } from "@tanstack/react-query";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const MAX_FILE_COUNT = 100;

const normalizeImageFile = async (file) => {
  try {
    const buffer = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let mimeType = file.type;

    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      mimeType = "image/jpeg";
    } else if (
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47
    ) {
      mimeType = "image/png";
    } else if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      mimeType = "image/webp";
    }

    if (mimeType === file.type) return file;

    return new File([file], file.name, {
      type: mimeType,
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.error("Error normalizing image MIME type:", error);
    return file;
  }
};

const AdminGallery = () => {
  const { RangePicker } = DatePicker;
  const setGettingStartedItemStatus = useAdminDataStore(
    (state) => state.setGettingStartedItemStatus,
  );

  const [dateRange, setDateRange] = useState([null, null]);
  const [files, setFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState({});

  const inputRef = useRef(null);
  const observerTarget = useRef(null);

  const groupData = useAdminDataStore(selectCurrentGroupData);
  const userData = useUserStore(selectUserDetails);
  const { getMemberById } = useMembersData();

  // ─── Infinite Query ───────────────────────────────────────────────────────
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "adminGalleryImages",
      groupData?.id,
      dateRange?.[0] ? dateRange[0].unix() : null,
      dateRange?.[1] ? dateRange[1].unix() : null,
    ],
    queryFn: ({ pageParam }) =>
      fetchAdminGalleryPagedImages({
        groupId: groupData?.id,
        dateRange,
        pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return { lastDocId: lastPage.lastDocId };
      }
      return undefined;
    },
    enabled: !!groupData?.id,
  });

  const allImages = data?.pages?.flatMap((page) => page.images) || [];

  // ─── Intersection Observer ────────────────────────────────────────────────
  useEffect(() => {
    const currentTarget = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Also trigger refetch after upload so getting started status is updated
  useEffect(() => {
    if (allImages.length > 0) {
      setGettingStartedItemStatus(
        groupData?.id,
        GUIDE_SETUP,
        TOPIC_UPLOAD_PHOTOS,
        true,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allImages.length, groupData?.id]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const extractPathFromUrl = (url) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const startIndex = decodedUrl.indexOf("/o/") + 3;
      const endIndex = decodedUrl.indexOf("?");
      return decodedUrl.substring(startIndex, endIndex);
    } catch (error) {
      console.error("Error extracting path:", error);
      return null;
    }
  };

  const isImageHeic = (file) =>
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name.toLowerCase().endsWith(".heic") ||
    file.name.toLowerCase().endsWith(".heif");

  const convertHeicToJpeg = async (file) => {
    try {
      if (!isImageHeic(file)) return file;
      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.9,
      });
      return new File(
        [convertedBlob],
        file.name.replace(/\.(heic|heif)$/i, ".jpg"),
        { type: "image/jpeg", lastModified: file.lastModified || Date.now() },
      );
    } catch (error) {
      console.error("Error converting HEIC to JPEG:", error);
      return file;
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const deleteSelectedImage = async (imageId, urlLarge, urlSmall) => {
    try {
      setDeletingImageId(imageId);
      setDeleteLoading({ [imageId]: true });

      const pathLarge =
        urlLarge?.length > 0 ? extractPathFromUrl(urlLarge) : "";
      const pathSmall =
        urlSmall?.length > 0 ? extractPathFromUrl(urlSmall) : "";

      const success = await deleteBothImageCopies(
        pathLarge,
        pathSmall,
        groupData?.id,
        imageId,
      );

      if (success) {
        toast.success("Image deleted successfully");
        setDeletingImageId(null);
        await refetch();
      } else {
        setDeletingImageId(null);
        toast.error("Failed to delete image");
        logger.error("Failed to delete image");
      }
    } catch (error) {
      toast.error("Failed to delete image");
      setDeletingImageId(null);
      logger.error("Error deleting image:", error);
    } finally {
      setDeleteLoading({ [imageId]: false });
    }
  };

  // ─── Upload ───────────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    try {
      let { files } = e.target;
      files = Array.from(files);
      if (files.length > MAX_FILE_COUNT) files = files.slice(0, MAX_FILE_COUNT);

      const loadPromises = files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.addEventListener("loadend", () =>
              resolve({ file, data: reader.result }),
            );
            reader.readAsDataURL(file);
          }),
      );

      const loadedFiles = await Promise.all(loadPromises);
      setFiles(loadedFiles);
      setShowUploadModal(true);
      e.target.value = null;
    } catch (error) {
      logger.error("Error uploading images:", error);
    }
  };

  const handleUploadImages = async () => {
    setIsUploading(true);

    try {
      const path = `/${groupData?.groupName}/gallery`;
      const uploadPromises = [];

      const largeImgOptions = {
        maxSizeMB: 2.5,
        maxWidthOrHeight: 2560,
        useWebWorker: true,
        alwaysKeepResolution: true,
        preserveExif: true,
      };

      const smallImgOptions = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 2560,
        useWebWorker: true,
        alwaysKeepResolution: true,
        preserveExif: true,
      };

      for (let i = 0; i < files.length; i++) {
        await new Promise((done) => setTimeout(done, 10));
        let originalImage = files[i].file;

        if (isImageHeic(originalImage)) {
          originalImage = await convertHeicToJpeg(originalImage);
        } else {
          originalImage = await normalizeImageFile(originalImage);
        }

        const lgCompressedImage = await imageCompression(
          originalImage,
          largeImgOptions,
        );
        const smCompressedImage = await imageCompression(
          originalImage,
          smallImgOptions,
        );

        uploadPromises.push(
          uploadImageSizes(
            smCompressedImage,
            lgCompressedImage,
            `${path}/${dayjs().valueOf()}`,
            dayjs().unix(),
            originalImage.lastModified || dayjs().unix(),
          ),
        );

        setProgress(((i + 1) * 100) / files.length);
      }

      const res = await Promise.all(uploadPromises);

      const imagesDataPromises = res.map(
        ({ downloadUrlSmall }) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () =>
              resolve({
                [downloadUrlSmall]: { width: img.width, height: img.height },
              });
            img.onerror = reject;
            img.src = downloadUrlSmall;
          }),
      );

      const imagesData = (await Promise.all(imagesDataPromises)).reduce(
        (acc, val) => ({ ...acc, ...val }),
        {},
      );

      const batch = writeBatch(db);

      res.forEach(
        ({ downloadUrlSmall, downloadUrlLarge, imageDate, timestamp }) => {
          const dimensions = imagesData[downloadUrlSmall];

          let uploaderName = userData?.profile?.displayName;
          if (!uploaderName) {
            uploaderName = getMemberById(userData?.profile?.uid)?.name;
          }

          const data = {
            favBy: [],
            imageUrl: downloadUrlSmall,
            imageUrlLg: downloadUrlLarge,
            updatedOn: dayjs().unix(),
            uploadDate: timestamp,
            uploaderName,
            userDocId: userData?.profile?.uid,
            dimensions: dimensions || {},
          };

          if (imageDate) data.imageDate = dayjs(imageDate).unix();

          const docRef = doc(
            collection(db, "gallery", groupData?.id, "images"),
          );
          batch.set(docRef, data);
        },
      );

      await batch.commit();

      await updateUploaderImageCount(
        groupData?.id,
        userData?.profile?.uid,
        "increment",
        res.length,
      );

      // Refetch from page 1 to reflect new uploads
      await refetch();
      setFiles([]);
      setShowUploadModal(false);
      setIsUploading(false);
      setProgress(0);
    } catch (err) {
      logger.error("Error uploading images:", err);
      setIsUploading(false);
      setShowUploadModal(false);
      setProgress(0);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const renderImages = () => {
    const imagesFinal = allImages.map((img) => ({
      id: img.id,
      src: img.imageUrl,
      srcLg: img.imageUrlLg,
      width: img.width,
      height: img.height,
      tags: [
        {
          value: dayjs.unix(img.uploadDate).format("Do MMM, YYYY"),
          title: dayjs.unix(img.uploadDate).format("Do MMM, YYYY"),
        },
      ],
      onDelete: () => deleteSelectedImage(img.id, img.imageUrlLg, img.imageUrl),
    }));

    if (imagesFinal.length === 0 && !isLoading) {
      return <NoImages inputRef={inputRef} />;
    }

    return (
      <>
        <ImageGallery
          loading={isLoading}
          images={imagesFinal}
          deletingImageId={deletingImageId}
          deleteLoading={deleteLoading}
        />
        {isFetchingNextPage && (
          <div className="gallery-grid" style={{ marginTop: "12px" }}>
            {Array.from({ length: 6 }, (_, index) => (
              <div key={`skeleton-${index}`} className="gallery-item-container">
                <div className="gallery-item-skeleton">
                  <Skeleton.Image active />
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={observerTarget} style={{ height: "20px" }} />
      </>
    );
  };

  return (
    <div className="col">
      <div style={{ padding: "12px" }}>
        <ScreenHeader
          title="Gallery"
          actions={
            <div>
              {isMobile() ? (
                <IconContainer
                  onClick={() => inputRef?.current.click()}
                  iconComponent={<PlusOutlined />}
                  iconStyle={{ fontSize: "16px" }}
                  disabled={isUploading}
                />
              ) : (
                <ChantButton onClick={() => inputRef?.current.click()}>
                  Upload
                </ChantButton>
              )}
            </div>
          }
        />

        <div className="gallery-description">
          <DescriptionText>
            Upload up to 100 photos at a time (try fewer if slow connection).
            Images compressed to max 2.5MB. Video support coming soon.
          </DescriptionText>
        </div>

        <div className="gallery-filters-header">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <RangePicker
              picker="month"
              value={dateRange}
              onChange={(value) => setDateRange(value || [null, null])}
              disabledDate={(current) =>
                current && current > dayjs().endOf("month")
              }
            />
            <div
              style={{
                visibility: "hidden",
                position: "absolute",
                pointerEvents: "none",
              }}
            >
              <input
                ref={(ref) => {
                  inputRef.current = ref;
                }}
                type="file"
                multiple
                accept="image/*"
                style={{ height: 0, width: 0 }}
                onChange={handleUpload}
                onBlur={() => inputRef?.current.blur()}
              />
            </div>
          </div>
        </div>

        <div>{renderImages()}</div>

        <UploadConfirmationModal
          showUploadModal={showUploadModal}
          files={files}
          isUploading={isUploading}
          progress={progress}
          removeImage={(index) => setFiles(files.filter((_, i) => i !== index))}
          onCancel={() => {
            setFiles([]);
            setShowUploadModal(false);
          }}
          onUploadImages={isUploading ? () => {} : handleUploadImages}
        />
      </div>
    </div>
  );
};

export default AdminGallery;
